/* eslint-disable metabase/no-color-literals */
import type { MetabaseThemeV2 } from "../../types";
import { DARK_THEME_ACCENT_COLORS } from "../accent-colors";
import { getBaseColorsForThemeDefinitionOnly } from "../base-colors";

const baseColors = getBaseColorsForThemeDefinitionOnly();

type BrandRamp = typeof baseColors.brand;

// `orion[110]` at 20% opacity. Kept off the alpha scale because `orionAlpha` is
// based on `orion[100]`.
const ORION_110_ALPHA_20 = "hsla(205, 63%, 5%, 0.2)";

// To be removed once the rename in GDGT-2517 is complete. Prefer the keys in
// `actualColors` below.
const getDeprecatedColors = (brand: BrandRamp) => ({
  "admin-navbar": baseColors.octopus[80],
  "admin-navbar-secondary": baseColors.octopus[60],
  "admin-navbar-inverse": baseColors.octopus[60],
  "background-brand": brand[90],
  "background-filter": baseColors.filter[90],
  "background-disabled": baseColors.orionAlphaInverse[10],
  "background-disabled-inverse": baseColors.orionAlpha[10],
  "background-error-secondary": baseColors.lobster[90],
  "background-highlight":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background-primary": baseColors.orion[100],
  "background-secondary": baseColors.orion[110],
  "background-tertiary": baseColors.orion[80],
  "background-primary-inverse": baseColors.orion[20],
  "background-secondary-inverse": baseColors.orion[30],
  "background-tertiary-inverse": baseColors.orion[70],
  overlay: baseColors.orionAlpha[70],
  "background-error": baseColors.lobster[90],
  "background-success": baseColors.palm[90],
  "background-success-secondary": baseColors.palm[70],
  brand: baseColors.blue[40],
  "brand-hover": brand[30],
  danger: baseColors.lobster[50],
  error: baseColors.lobster[50],
  filter: baseColors.octopus[40],
  focus: baseColors.blue[70],
  "icon-brand": brand[40], // Matches text-brand
  "icon-brand-inverse": brand[50],
  "icon-primary": baseColors.orionAlphaInverse[80], // Matches text-primary
  "icon-primary-inverse": baseColors.orionAlpha[80], // Matches text-primary-inverse
  "icon-disabled": baseColors.orionAlphaInverse[40], // Matches text-disabled
  "icon-disabled-inverse": baseColors.orionAlpha[40], // Matches text-disabled-inverse
  "icon-secondary": baseColors.orionAlphaInverse[60], // Matches text-secondary
  "icon-secondary-inverse": baseColors.orionAlpha[60], // Matches text-secondary-inverse
  "illustration-brand-secondary": brand[80],
  "illustration-brand-tertiary": brand[90],
  "metabase-brand": baseColors.blue[40],
  "metabase-brand-hover": baseColors.blue[50],
  "saturated-blue": baseColors.ocean[40],
  "saturated-green": baseColors.palm[40],
  "saturated-purple": baseColors.octopus[40],
  "saturated-red": baseColors.lobster[40],
  "saturated-yellow": baseColors.dubloon[30],
  shadow: ORION_110_ALPHA_20,
  "success-secondary": baseColors.palm[40],
  success: baseColors.palm[50],
  summarize: baseColors.palm[40],
  "syntax-parameters-active": baseColors.mango[90],
  "syntax-parameters": baseColors.mango[40],
  "text-tertiary": baseColors.orionAlphaInverse[40],
  "text-tertiary-inverse": baseColors.orionAlpha[40],
  "text-hover": brand[30],
  "text-secondary-opaque": baseColors.orion[20],
  warning: baseColors.dubloon[30],
  "background-warning": baseColors.dubloon[90],
  "background-warning-secondary": baseColors.dubloon[70],
  info: baseColors.orion[50],
  "background-info": baseColors.orion[90],
  white: baseColors.orion[110],
  border: baseColors.orionAlphaInverse[20],
  "border-strong": baseColors.orionAlphaInverse[30],
  "border-stronger": baseColors.orionAlphaInverse[50],
  "border-subtle": baseColors.orionAlphaInverse[10],
  gold: baseColors.gold,
  "upsell-primary": baseColors.ocean[30],
  "upsell-secondary": baseColors.ocean[80],
  "upsell-gem": baseColors.upsellGem,
});

const getActualColors = (brand: BrandRamp) => ({
  "background_page-filter": baseColors.filter[90],
  "background_page-highlighted":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_page-primary": baseColors.orion[95],
  "background_page-primary-inverse": baseColors.white,
  "background_page-secondary": baseColors.orion[100],
  "background_page-secondary-inverse": baseColors.orion[5],
  "background_page-tertiary": baseColors.orion[110],
  "background_page-tertiary-inverse": baseColors.orion[10],
  "background_surface-brand-strong": brand[20],
  "background_surface-brand-strong-hover": brand[10],
  "background_surface-brand-strong-pressed": brand[20],
  "background_surface-brand-subtle":
    "color-mix(in srgb, var(--mb-color-core-brand) 10%, transparent)",
  "background_surface-brand-subtle-hover":
    "color-mix(in srgb, var(--mb-color-core-brand), black 50%)",
  "background_surface-brand-subtle-pressed":
    "color-mix(in srgb, var(--mb-color-core-brand), black 75%)",
  "background_surface-disabled": baseColors.orionAlphaInverse[10],
  "background_surface-disabled-inverse": baseColors.orionAlpha[10],
  "background_surface-error": baseColors.lobster[90],
  "background_surface-error-subtle": baseColors.lobster[90],
  "background_surface-hover":
    "color-mix(in srgb, var(--mb-color-core-brand) 7%, transparent)",
  "background_surface-primary": baseColors.orion[95], // Matches background_page-primary
  "background_surface-primary-hover": baseColors.orionAlphaInverse[20],
  "background_surface-primary-inverse": baseColors.white, // Matches background_page-primary-inverse
  "background_surface-primary-inverse-hover": baseColors.orionAlpha[10],
  "background_surface-primary-inverse-pressed": baseColors.orionAlpha[20],
  "background_surface-primary-pressed": baseColors.orionAlphaInverse[10],
  "background_surface-secondary": baseColors.orionAlphaInverse[10],
  "background_surface-secondary-hover": baseColors.orionAlphaInverse[20],
  "background_surface-secondary-inverse": baseColors.orionAlpha[10],
  "background_surface-secondary-inverse-hover": baseColors.orionAlpha[20],
  "background_surface-secondary-inverse-pressed": baseColors.orionAlpha[30],
  "background_surface-secondary-pressed": baseColors.orionAlphaInverse[10],
  "background_surface-selected":
    "color-mix(in srgb, var(--mb-color-core-brand) 21%, transparent)",
  "background_surface-success": baseColors.palm[90],
  "background_surface-warning": baseColors.dubloon[90],
  "background_surface-warning-strong": baseColors.dubloon[70],
  "border-brand": brand[80],
  "border-filter": baseColors.filter[80],
  "border-highlight": brand[30],
  "border-neutral": baseColors.orionAlphaInverse[20],
  "border-neutral-inverse-strong": baseColors.orionAlpha[30],
  "border-neutral-strong": baseColors.orionAlphaInverse[30],
  "border-neutral-strongest": baseColors.orionAlphaInverse[50],
  "border-neutral-subtle": baseColors.orionAlphaInverse[10],
  "border-on_dark": baseColors.orionAlphaInverse[20],
  "button-default-neutral-default": baseColors.orion[95], // Matches background_surface-primary
  "button-default-neutral-hover": baseColors.orionAlphaInverse[20], // Matches background_surface-primary-hover
  "button-default-neutral-pressed": baseColors.orionAlphaInverse[10], // Matches background_surface-primary-pressed
  "button-filled-brand-default": brand[20],
  "button-filled-brand-hover": brand[10],
  "button-filled-brand-pressed": brand[20],
  "button-filled-filter-default": baseColors.filter[20],
  "button-filled-filter-hover": baseColors.filter[10],
  "button-filled-filter-pressed": baseColors.filter[20],
  "button-filled-negative-default": baseColors.lobster[20],
  "button-filled-negative-hover": baseColors.lobster[10],
  "button-filled-negative-pressed": baseColors.lobster[20],
  "button-filled-positive-default": baseColors.palm[20],
  "button-filled-positive-hover": baseColors.palm[10],
  "button-filled-positive-pressed": baseColors.palm[20],
  "button-filled-warning-default": baseColors.dubloon[20],
  "button-filled-warning-hover": baseColors.dubloon[10],
  "button-filled-warning-pressed": baseColors.dubloon[20],
  "button-light-brand-default": baseColors.orionAlphaInverse[10],
  "button-light-brand-hover": baseColors.orionAlphaInverse[20],
  "button-light-brand-pressed": baseColors.orionAlphaInverse[10],
  "button-light-filter-default": baseColors.orionAlphaInverse[10],
  "button-light-filter-hover": baseColors.orionAlphaInverse[20],
  "button-light-filter-pressed": baseColors.orionAlphaInverse[10],
  "button-light-negative-default": baseColors.orionAlphaInverse[10],
  "button-light-negative-hover": baseColors.orionAlphaInverse[20],
  "button-light-negative-pressed": baseColors.orionAlphaInverse[10],
  "button-light-neutral-default": baseColors.orionAlphaInverse[10], // Matches background_surface-secondary
  "button-light-neutral-hover": baseColors.orionAlphaInverse[20], // Matches background_surface-secondary-hover
  "button-light-neutral-pressed": baseColors.orionAlphaInverse[10], // Matches background_surface-secondary-pressed
  "button-light-positive-default": baseColors.orionAlphaInverse[10],
  "button-light-positive-hover": baseColors.orionAlphaInverse[20],
  "button-light-positive-pressed": baseColors.orionAlphaInverse[10],
  "button-on_dark-disabled": baseColors.orionAlphaInverse[10],
  "button-on_dark-primary-default": baseColors.white,
  "button-on_dark-primary-hover": baseColors.orion[20],
  "button-on_dark-primary-pressed": baseColors.orion[30],
  "button-on_dark-secondary-default": baseColors.orionAlphaInverse[10],
  "button-on_dark-secondary-hover": baseColors.orionAlphaInverse[20],
  "button-on_dark-secondary-pressed": baseColors.orionAlphaInverse[10],
  "button-subtle-brand-default": baseColors.transparent,
  "button-subtle-brand-hover": baseColors.orionAlphaInverse[20],
  "button-subtle-brand-pressed": baseColors.orionAlphaInverse[10],
  "button-subtle-negative-default": baseColors.transparent,
  "button-subtle-negative-hover": baseColors.orionAlphaInverse[20],
  "button-subtle-negative-pressed": baseColors.orionAlphaInverse[10],
  "button-subtle-neutral-default": baseColors.transparent,
  "button-subtle-neutral-hover": baseColors.orionAlphaInverse[20],
  "button-subtle-neutral-pressed": baseColors.orionAlphaInverse[10],
  "button-subtle-positive-default": baseColors.transparent,
  "button-subtle-positive-hover": baseColors.orionAlphaInverse[20],
  "button-subtle-positive-pressed": baseColors.orionAlphaInverse[10],
  "button_label-default-neutral-default": baseColors.orionAlphaInverse[80], // Matches text-primary
  "button_label-filled-brand-default": brand[80],
  "button_label-filled-filter-default": baseColors.filter[80],
  "button_label-filled-negative-default": baseColors.lobster[80],
  "button_label-filled-positive-default": baseColors.palm[80],
  "button_label-filled-warning-default": baseColors.lobster[80],
  "button_label-light-brand-default": brand[30],
  "button_label-light-brand-hover": brand[20],
  "button_label-light-filter-default": baseColors.filter[30],
  "button_label-light-filter-hover": baseColors.filter[20],
  "button_label-light-negative-default": baseColors.lobster[30],
  "button_label-light-negative-hover": baseColors.lobster[20],
  "button_label-light-neutral-default": baseColors.orionAlphaInverse[80], // Matches text-primary
  "button_label-light-positive-default": baseColors.palm[30],
  "button_label-light-positive-hover": baseColors.palm[20],
  "button_label-on_dark-disabled": baseColors.orionAlphaInverse[40],
  "button_label-on_dark-primary": baseColors.orionAlpha[80],
  "button_label-on_dark-secondary": baseColors.white,
  "button_label-subtle-brand-default": brand[30],
  "button_label-subtle-brand-hover": brand[20],
  "button_label-subtle-negative-default": baseColors.lobster[30],
  "button_label-subtle-negative-hover": baseColors.lobster[20],
  "button_label-subtle-neutral-default": baseColors.orionAlphaInverse[80], // Matches text-primary
  "button_label-subtle-positive-default": baseColors.palm[30],
  "button_label-subtle-positive-hover": baseColors.palm[20],
  "core-blue-saturated": baseColors.ocean[40],
  "core-brand-hover": brand[30],
  "core-filter-strong": baseColors.filter[20],
  "core-gold": baseColors.gold,
  "core-green-saturated": baseColors.palm[40],
  "core-info": baseColors.orion[50],
  "core-metabase_brand": baseColors.blue[40],
  "core-metabase_brand-hover": baseColors.blue[50],
  "core-purple-saturated": baseColors.octopus[40],
  "core-red-saturated": baseColors.lobster[40],
  "core-white": baseColors.orion[110],
  "core-white_constant": baseColors.white,
  "core-yellow-saturated": baseColors.dubloon[30],
  "feedback-negative": baseColors.lobster[50],
  "feedback-negative-strong": baseColors.lobster[30],
  "feedback-neutral-strong": baseColors.orionAlphaInverse[30],
  "feedback-positive": baseColors.palm[50],
  "feedback-positive-selected": baseColors.palm[40],
  "feedback-positive-strong": baseColors.palm[20],
  "feedback-warning": baseColors.dubloon[30],
  "feedback-warning-strong": baseColors.dubloon[20],
  "illustration-secondary-brand": brand[80],
  "illustration-tertiary-brand": brand[90],
  "input-background": baseColors.orionAlphaInverse[10],
  "input-focus": baseColors.blue[70],
  "modal-overlay": baseColors.orionAlpha[70],
  "navbar-admin": baseColors.octopus[80],
  "navbar-admin-inverse": baseColors.octopus[60],
  "navbar-admin-secondary": baseColors.octopus[60],
  "segmented_control-hover": baseColors.orionAlphaInverse[30],
  "segmented_control-pressed": baseColors.orionAlphaInverse[20],
  "segmented_control-selected": baseColors.orionAlphaInverse[20],
  "shadow-default": ORION_110_ALPHA_20,
  "switch-checked": brand[30],
  "switch-disabled": baseColors.orionAlphaInverse[20],
  "switch-unchecked": baseColors.orionAlphaInverse[40],
  "switch_thumb-disabled": baseColors.orionAlphaInverse[40],
  "text-brand": brand[40],
  "text-brand-hover": brand[30],
  "text-brand-strong": brand[20],
  "text-brand-strong-hover": brand[10],
  "text-disabled": baseColors.orionAlphaInverse[40],
  "text-disabled-inverse": baseColors.orionAlpha[40],
  "text-filter": baseColors.filter[30],
  "text-primary": baseColors.orionAlphaInverse[80],
  "text-primary-inverse": baseColors.orionAlpha[80],
  "text-secondary": baseColors.orionAlphaInverse[60],
  "text-secondary-inverse": baseColors.orionAlpha[60],
  "text-selected": baseColors.white,
  "text-syntax-parameter": baseColors.mango[40],
  "text-syntax-parameter-active": baseColors.mango[90],
  "tooltip-background": baseColors.orion[70],
  "tooltip-background-focused": baseColors.orion[80],
  "tooltip-text": baseColors.orionAlphaInverse[80],
  "tooltip-text-secondary": baseColors.orionAlphaInverse[60],
});

/**
 * The default dark theme for Metabase.
 */
export const getDarkTheme = (
  brand: BrandRamp = baseColors.brand,
): MetabaseThemeV2 => ({
  version: 2,
  chartColors: DARK_THEME_ACCENT_COLORS,
  colors: {
    ...getDeprecatedColors(brand),
    ...getActualColors(brand),
  },
});
